import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  List,
  ListItem,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import axios from "axios";
import HttpService from "../services/httpService";

const AssignCRMUserDialog = ({
  open,
  onClose,
  leadId,
  leadNumber,
  onAssignSuccess,
}) => {
  const [crmUsers, setCrmUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState("");

  // Fetch CRM users when dialog opens
  useEffect(() => {
    if (open) {
      fetchCRMUsers();
    }
  }, [open]);

  const fetchCRMUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await HttpService.getFactoryCRMUsers();
      // console.log("CRM Users Response:", response);
      setCrmUsers(response.data.data);
    } catch (err) {
      console.error("Error fetching CRM users:", err);
      setError("Failed to load CRM users");
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUser) {
      setError("Please select a CRM user");
      return;
    }

    setAssigning(true);
    setError("");
    const data = {
      leadId,
      userId: selectedUser,
    };
    try {
      const response = await HttpService.assignFactoryCRMToLead(data);

      if (response.data.success) {
        if (onAssignSuccess) {
          onAssignSuccess(response.data.data);
        }
        onClose();
        setSelectedUser("");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign CRM user");
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    if (!assigning) {
      setSelectedUser("");
      setError("");
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xs"  // Changed from "md" to "xs" for smaller size
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,  // Rounded corners
          minWidth: { xs: '90%', sm: '400px' }  // Minimum width
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2 }}>
        <Typography variant="h6" fontSize="1.25rem">
          Assign CRM User
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: "0.875rem" }}>
            {error}
          </Alert>
        )}

        {/* Lead Number (Optional - uncomment if needed) */}
        {/* {leadNumber && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: "0.875rem" }}>
            Lead: <strong>{leadNumber}</strong>
          </Typography>
        )} */}

        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontSize: "0.875rem" }}>
          Select CRM User ({crmUsers.length} users)
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <RadioGroup
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <List sx={{ p: 0 }}>
              {crmUsers?.map((user) => (
                <ListItem
                  key={user._id}
                  button
                  onClick={() => setSelectedUser(user._id)}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    mb: 1,
                    p: 1,
                    bgcolor: selectedUser === user._id ? "#e3f2fd" : "white",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: selectedUser === user._id ? "#e3f2fd" : "#f5f5f5",
                    },
                  }}
                >
                  <FormControlLabel
                    value={user._id}
                    control={<Radio size="small" />}
                    label=""
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user.name}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </RadioGroup>
        )}

        {crmUsers.length === 0 && !loading && (
          <Alert severity="warning" sx={{ fontSize: "0.875rem" }}>
            No CRM users found
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={assigning}
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          disabled={assigning || !selectedUser || crmUsers.length === 0}
          size="small"
          sx={{ minWidth: 100 }}
        >
          {assigning ? <CircularProgress size={20} /> : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignCRMUserDialog;